// Dialog component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.5

import React from 'react'
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { tokens } from '../../../styles/tokens'
import { Button } from '../button'
import {
  DialogProps,
  DialogHeaderProps,
  DialogContentProps,
  DialogActionsProps,
  ConfirmDialogProps,
  DialogSize,
} from './dialog.types'

// Size configurations
const sizeWidths: Record<DialogSize, number> = {
  xs: 444,
  sm: 600,
  md: 900,
  lg: 1200,
}

// Styled dialog paper
const StyledDialog = styled(MuiDialog, {
  shouldForwardProp: (prop) => prop !== 'dialogSize',
})<{ dialogSize: DialogSize }>(({ dialogSize }) => ({
  '& .MuiDialog-paper': {
    borderRadius: tokens.radius.lg,
    maxWidth: sizeWidths[dialogSize],
    width: '100%',
  },
}))

// Styled header
const StyledHeader = styled(DialogTitle)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: 24,
  paddingBottom: 16,
})

// Styled content
const StyledContent = styled(MuiDialogContent)({
  padding: '0 24px 24px',
})

// Styled actions
const StyledActions = styled(MuiDialogActions)({
  padding: '16px 24px',
  borderTop: `1px solid ${tokens.colors.grey[200]}`,
  gap: 12,
  justifyContent: 'flex-end',
})

// Main Dialog component
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  size = 'sm',
  children,
  sx,
}) => {
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      dialogSize={size}
      sx={sx}
    >
      {children}
    </StyledDialog>
  )
}

// DialogHeader component
export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  subtitle,
  showClose = true,
  onClose,
  sx,
}) => {
  return (
    <StyledHeader sx={sx}>
      <Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            fontFamily: '"Montserrat", sans-serif',
            color: tokens.colors.grey[900],
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: 14,
              color: tokens.colors.grey[500],
              marginTop: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {showClose && onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: tokens.colors.grey[400],
            '&:hover': {
              color: tokens.colors.grey[600],
            },
          }}
        >
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />
        </IconButton>
      )}
    </StyledHeader>
  )
}

// DialogContent component
export const DialogContent: React.FC<DialogContentProps> = ({ children, sx }) => {
  return <StyledContent sx={sx}>{children}</StyledContent>
}

// DialogActions component
export const DialogActions: React.FC<DialogActionsProps> = ({ children, sx }) => {
  return <StyledActions sx={sx}>{children}</StyledActions>
}

// ConfirmDialog component
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} size="xs">
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent>
        <Typography
          sx={{
            fontSize: 14,
            color: tokens.colors.grey[600],
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

Dialog.displayName = 'Dialog'
DialogHeader.displayName = 'DialogHeader'
DialogContent.displayName = 'DialogContent'
DialogActions.displayName = 'DialogActions'
ConfirmDialog.displayName = 'ConfirmDialog'

export default Dialog
export type {
  DialogProps,
  DialogHeaderProps,
  DialogContentProps,
  DialogActionsProps,
  ConfirmDialogProps,
  DialogSize,
  ConfirmVariant,
} from './dialog.types'
